import { useEffect } from "react";

type JsonLdObject = {
  "@context": "https://schema.org";
  "@type": string;
  [key: string]: unknown;
};

interface Props {
  title: string;
  description: string;
  canonical?: string;
  jsonLd?: JsonLdObject | JsonLdObject[];
}

const SITE_ORIGIN = "https://taidotech.com.au";

function withTrailingSlash(path: string): string {
  if (path === "/" || path.endsWith("/")) return path;
  // Don't add trailing slash to anything that looks like a file (has an extension).
  if (/\.[a-z0-9]+$/i.test(path)) return path;
  return `${path}/`;
}

function buildCanonical(canonical?: string): string | undefined {
  if (!canonical) return undefined;
  if (canonical.startsWith("http")) return withTrailingSlash(canonical);
  const clean = canonical.startsWith("/") ? canonical : `/${canonical}`;
  return `${SITE_ORIGIN}${withTrailingSlash(clean)}`;
}

declare global {
  // eslint-disable-next-line no-var
  var __SSR_HEAD__:
    | {
        title?: string;
        description?: string;
        canonical?: string;
        jsonLd?: JsonLdObject | JsonLdObject[];
      }
    | undefined;
}

const OG_IMAGE_PATH = "opengraph.jpg";

function setMeta(name: string, content: string, attr: "name" | "property" = "name") {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function PageMeta({ title, description, canonical, jsonLd }: Props) {
  const canonicalUrl = buildCanonical(canonical);

  if (typeof window === "undefined") {
    globalThis.__SSR_HEAD__ = {
      title,
      description,
      canonical: canonicalUrl,
      jsonLd,
    };
  }

  useEffect(() => {
    const ogImage = new URL(`${import.meta.env.BASE_URL}${OG_IMAGE_PATH}`, window.location.origin).href;
    document.title = title;
    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:image", ogImage, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);
    if (canonicalUrl) {
      setLink("canonical", canonicalUrl);
      setMeta("og:url", canonicalUrl, "property");
    } else {
      document.querySelector('link[rel="canonical"]')?.remove();
      document.querySelector('meta[property="og:url"]')?.remove();
    }
    document.querySelectorAll('script[data-page-jsonld="true"]').forEach((el) => el.remove());
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      for (const item of items) {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.pageJsonld = "true";
        script.text = JSON.stringify(item);
        document.head.appendChild(script);
      }
    }
  }, [title, description, canonicalUrl, jsonLd]);

  return null;
}

export function breadcrumbJsonLd(items: { name: string; path: string }[]): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_ORIGIN}${item.path === "/" ? "/" : item.path.endsWith("/") ? item.path : `${item.path}/`}`,
    })),
  };
}

export const ORGANIZATION_JSONLD: JsonLdObject = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Taidotech",
  legalName: "Taidotech Pty Ltd",
  url: `${SITE_ORIGIN}/`,
  logo: `${SITE_ORIGIN}/assets/logos/taido_primary.svg`,
  email: "info@taidotech.com.au",
  telephone: "+61 3 4232 6950",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Office 1, Level 1, 343 Pakington Street",
    addressLocality: "Newtown",
    addressRegion: "VIC",
    postalCode: "3220",
    addressCountry: "AU",
  },
  areaServed: "AU",
  sameAs: [
    "https://abr.business.gov.au/ABN/View/30654263279",
    "https://buy.nsw.gov.au/supplier/profile/12437661",
  ],
  description:
    "Taidotech designs, builds and operates AI platforms and process automation for regulated Australian organisations — aged care, healthcare, NDIS and government.",
};

export function serviceJsonLd(name: string, description: string, path: string): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url: `${SITE_ORIGIN}${path === "/" || path.endsWith("/") ? path : `${path}/`}`,
    provider: {
      "@type": "Organization",
      name: "Taidotech",
      url: `${SITE_ORIGIN}/`,
    },
    areaServed: "AU",
  };
}

export function articleJsonLd(post: {
  title: string;
  description: string;
  datePublishedISO: string;
  slug: string;
}): JsonLdObject {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    datePublished: post.datePublishedISO,
    author: { "@type": "Organization", name: "Taidotech" },
    publisher: {
      "@type": "Organization",
      name: "Taidotech",
      logo: {
        "@type": "ImageObject",
        url: `${SITE_ORIGIN}/assets/logos/taido_primary.svg`,
      },
    },
    mainEntityOfPage: `${SITE_ORIGIN}/news/${post.slug}/`,
  };
}
